import { useEffect, useState } from "react";
import { api, photoUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function Wishes() {
  const [wishes, setWishes] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { photos: [ids], index }
  const isAdmin = !!localStorage.getItem("admin_token");

  const load = () => api.get("/wishes").then((r) => setWishes(r.data));
  useEffect(() => { load(); }, []);

  const deleteWish = async (id) => {
    if (!window.confirm("Wens verwijderen?")) return;
    try { await api.delete(`/wishes/${id}`); toast.success("Verwijderd"); load(); }
    catch { toast.error("Mislukt"); }
  };

  const openLightbox = (photos, index) => setLightbox({ photos, index });
  const closeLightbox = () => setLightbox(null);
  const nextPhoto = (e) => {
    e?.stopPropagation();
    setLightbox((lb) => lb && { ...lb, index: (lb.index + 1) % lb.photos.length });
  };
  const prevPhoto = (e) => {
    e?.stopPropagation();
    setLightbox((lb) => lb && { ...lb, index: (lb.index - 1 + lb.photos.length) % lb.photos.length });
  };

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextPhoto();
      if (e.key === "ArrowLeft") prevPhoto();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-20" data-testid="wishes-page">
      <div className="text-center mb-16">
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]">I desideri degli amici</p>
        <h1 className="font-script text-6xl sm:text-7xl text-[#006A71] mt-3">Wensen</h1>
        <div className="section-divider"><span className="section-divider-dot" /></div>
        <p className="text-[#4A6165] mt-4 max-w-xl mx-auto leading-relaxed">
          Een verzameling boodschappen, herinneringen en lieve woorden van jullie vrienden.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10" data-testid="wishes-list">
        {wishes.map((w, i) => (
          <motion.div
            key={w.id}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05 }}
            className="polaroid relative"
            data-testid={`wish-card-${w.id}`}
          >
            {w.photo_ids && w.photo_ids.length > 0 && (
              <div className={`grid gap-1 mb-4 ${w.photo_ids.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                {w.photo_ids.slice(0, 4).map((pid, idx) => (
                  <button
                    key={pid}
                    type="button"
                    onClick={() => openLightbox(w.photo_ids, idx)}
                    className="overflow-hidden group/photo focus:outline-none"
                    data-testid={`wish-photo-${w.id}-${idx}`}
                  >
                    <img
                      src={photoUrl(pid)}
                      alt=""
                      className={`w-full object-cover cursor-zoom-in transition-transform duration-500 group-hover/photo:scale-105 ${w.photo_ids.length === 1 ? "h-48" : "h-24"}`}
                    />
                  </button>
                ))}
              </div>
            )}
            <p className="font-serif text-lg italic leading-relaxed text-[#1F3E42]">"{w.message}"</p>
            <div className="mt-5 pt-4 border-t border-[#1F3E42]/10">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">— {w.friend_name}</p>
            </div>
            {isAdmin && (
              <div className="absolute bottom-1 right-2 flex gap-2 text-[10px]">
                <button onClick={() => togglePin(w.id)} className="underline">pin</button>
                <button onClick={() => deleteWish(w.id)} className="underline text-red-600">verwijder</button>
              </div>
            )}
          </motion.div>
        ))}
      </div>
      {wishes.length === 0 && (
        <p className="text-center font-script text-3xl text-[#4A6165] mt-10">Nog geen wensen verzameld.</p>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-[#1F3E42]/85 backdrop-blur-sm flex items-center justify-center p-5"
          onClick={closeLightbox}
          data-testid="wish-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-5 right-5 text-[#FAF6EF] hover:text-white p-2"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          {lightbox.photos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-4 sm:left-8 z-10 text-[#FAF6EF] hover:text-white p-3 bg-black/40 rounded-full"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-4 sm:right-8 z-10 text-[#FAF6EF] hover:text-white p-3 bg-black/40 rounded-full"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <motion.div
            key={lightbox.index}
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="polaroid max-w-4xl w-full" onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photoUrl(lightbox.photos[lightbox.index])}
              alt=""
              className="w-full max-h-[75vh] object-contain"
            />
            {lightbox.photos.length > 1 && (
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#006A71] text-center mt-4">
                {lightbox.index + 1} / {lightbox.photos.length}
              </p>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
